import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, Heart, Users, Award, MessageCircle, Share2, BookOpen, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { UnifiedHeader } from '../components/layout';
import { Logo } from '../components/ui/Logo';
import { usePublicAnnouncements } from '../hooks/useAnnouncements';
import { AnnouncementType } from '../types/announcements';
import { MarkdownRenderer } from '../components/ui/MarkdownRenderer';
import { SEO } from '../components/seo/SEO';
import { PAGE_META } from '../config/seo';

// interface NewsArticle {
//   id: number;
//   title: string;
//   excerpt: string;
//   content: string;
//   date: string;
//   category: string;
//   author: string;
//   image?: string;
//   featured?: boolean;
//   tags?: string[];
// }

// Component for handling content expansion/truncation
interface ExpandableContentProps {
  content: string;
  maxLength: number;
  proseColor: string;
  className?: string;
  cardId: string;
  expandedCards: Set<string>;
  onToggleExpand: (cardId: string) => void;
}

const ExpandableContent: React.FC<ExpandableContentProps> = ({
  content,
  maxLength,
  proseColor,
  className,
  cardId,
  expandedCards,
  onToggleExpand
}) => {
  const isExpanded = expandedCards.has(cardId);
  const shouldTruncate = content.length > maxLength;
  const displayContent = shouldTruncate && !isExpanded
    ? content.substring(0, maxLength) + '...'
    : content;

  return (
    <div>
      <div className={`transition-all duration-300 ${isExpanded ? 'opacity-100' : 'opacity-100'}`}>
        <MarkdownRenderer
          content={displayContent}
          proseColor={proseColor as any}
          className={className || ''}
        />
      </div>
      {shouldTruncate && (
        <button
          onClick={() => onToggleExpand(cardId)}
          className="mt-3 px-3 py-1 text-brand hover:text-brand-hover hover:bg-brand-light font-medium text-sm transition-colors duration-200 flex items-center space-x-1 rounded-md border border-border hover:border-border-strong"
        >
          <span>{isExpanded ? 'Show Less' : 'Read More'}</span>
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 transition-transform duration-200" />
          ) : (
            <ChevronDown className="w-4 h-4 transition-transform duration-200" />
          )}
        </button>
      )}
    </div>
  );
};

const NewsPageWarm: React.FC = () => {
  const navigate = useNavigate();

  // State for managing which cards are expanded
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());

  // Toggle card expansion
  const toggleCardExpansion = (cardId: string) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(cardId)) {
        newSet.delete(cardId);
      } else {
        newSet.add(cardId);
      }
      return newSet;
    });
  };

  // Fetch all public announcements and separate featured/recent on frontend
  const { data: allData } = usePublicAnnouncements({
    limit: 20 // Get more to ensure we have content
  });

  // Separate featured and recent announcements
  const featuredAnnouncements = allData?.announcements?.filter(a => a.isFeatured) || [];
  const recentAnnouncements = allData?.announcements?.filter(a => !a.isFeatured) || [];

  // If no featured announcements, use the most recent ones as featured
  const displayFeatured = featuredAnnouncements.length > 0
    ? featuredAnnouncements.slice(0, 3)
    : (allData?.announcements?.slice(0, 3) || []);

  const displayRecent = featuredAnnouncements.length > 0
    ? recentAnnouncements.slice(0, 6)
    : (allData?.announcements?.slice(3, 9) || []);


  /*
  const featuredNews: NewsArticle[] = [
    {
      id: 1,
      title: "2025 Family Reunion Planning Underway",
      excerpt: "Exciting updates on our biggest celebration yet, featuring special activities for all generations and a tribute to our founding members.",
      content: "Our planning committee has been working tirelessly to create an unforgettable experience for the 2025 Redd Family Reunion...",
      date: "2024-12-15",
      category: "Reunion Updates",
      author: "Patricia Redd Williams",
      featured: true,
      image: "/images/family-hero.jpeg",
      tags: ["Reunion", "Celebration", "Family"]
    },
    {
      id: 2,
      title: "New Family Members Join Our Association",
      excerpt: "We're thrilled to welcome five new family branches who have recently connected with our association through genealogy research.",
      content: "Through the power of modern genealogy and DNA research, we've been blessed to welcome new branches of the Redd family tree...",
      date: "2024-12-10",
      category: "Family News",
      author: "Sarah Redd Johnson",
      image: "/images/news-gathering.jpeg",
      tags: ["New Members", "Genealogy", "Family Tree"]
    },
    {
      id: 3,
      title: "Community Service Initiative Launch",
      excerpt: "The Redd Family Association announces a new community outreach program focusing on education and youth development.",
      content: "As part of our commitment to giving back to the communities that have supported us, we're launching our first official community service initiative...",
      date: "2024-12-05",
      category: "Community",
      author: "Michael Redd",
      tags: ["Community Service", "Youth", "Education"]
    }
  ];
  */

  /*
  const recentNews: NewsArticle[] = [
    {
      id: 4,
      title: "Annual Scholarship Recipients Announced",
      excerpt: "Three outstanding young family members receive educational scholarships to pursue their academic dreams.",
      content: "We're proud to announce this year's scholarship recipients who exemplify our family values of education and excellence...",
      date: "2024-11-28",
      category: "Education",
      author: "David Redd",
      tags: ["Scholarship", "Education", "Youth"]
    },
    {
      id: 5,
      title: "Digital Family Archive Project Update",
      excerpt: "Progress report on our ongoing effort to digitize and preserve family photos, documents, and stories for future generations.",
      content: "Our digital preservation project continues to make remarkable progress in safeguarding our family's precious memories...",
      date: "2024-11-20",
      category: "Heritage",
      author: "Sarah Redd Johnson",
      tags: ["Digital Archive", "Heritage", "Preservation"]
    },
    {
      id: 6,
      title: "Holiday Traditions Recipe Collection",
      excerpt: "Family members share beloved holiday recipes that have been passed down through generations of Redd family celebrations.",
      content: "From Grandma Patience's famous sweet potato pie to Uncle David's special holiday punch, our family recipes tell stories of love and tradition...",
      date: "2024-11-15",
      category: "Traditions",
      author: "Patricia Redd Williams",
      tags: ["Recipes", "Traditions", "Holidays"]
    }
  ];
  */

  /*
  const getCategoryColor = (category: string) => {
    const colors = {
      "Reunion Updates": "amber",
      "Family News": "orange",
      "Community": "red",
      "Education": "amber",
      "Heritage": "orange",
      "Traditions": "red"
    };
    return colors[category as keyof typeof colors] || "amber";
  };
  */

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get announcement type icon and display
  const getTypeDisplay = (type: AnnouncementType) => {
    const typeConfig = {
      meeting: { icon: Users, label: 'Meeting' },
      birthday: { icon: Heart, label: 'Birthday' },
      event: { icon: Calendar, label: 'Event' },
      memorial: { icon: Heart, label: 'Memorial' },
      fundraiser: { icon: Heart, label: 'Fundraiser' },
      general: { icon: Info, label: 'General' }
    };
    return typeConfig[type] || typeConfig.general;
  };

  const getWarmValueClasses = () => {
    return {
      cardBg: 'bg-surface-1',
      hoverShadow: 'hover:shadow-md',
      iconBg: 'bg-brand',
      dotBg: 'bg-brand',
      textColor: 'text-text-primary',
      border: 'border-border'
    };
  };

  return (
    <div className="min-h-screen bg-surface-0 text-text-primary">
      <SEO title={PAGE_META['/news'].title} description={PAGE_META['/news'].description} path="/news" />
      <UnifiedHeader variant="heritage" currentPage="News" />

      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden mt-16">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-black/40 z-10"></div>
          <div
            className="w-full h-full bg-cover transform scale-110"
            style={{
              backgroundImage: "url('/images/family-hero.jpeg')",
              backgroundPosition: '50% 80%',
              filter: 'blur(0.5px)'
            }}
          ></div>
        </div>

        <div className="relative z-20 text-center max-w-4xl px-4">
          <p className="text-white/70 text-sm font-medium mb-4">Latest News</p>
          <h1 className="text-4xl md:text-6xl font-display font-semibold mb-6 text-white leading-tight tracking-wide" style={{
            textShadow: '2px 2px 6px rgba(0,0,0,0.8)',
            letterSpacing: '0.02em'
          }}>
            Family News & Updates
          </h1>
          <p className="text-lg text-white/80 leading-relaxed" style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.7)' }}>
            Stay connected with the latest happenings, celebrations, and important updates from the Redd Family Association.
          </p>
        </div>
      </section>

      {/* Featured News Section */}
      <section className="py-16 bg-surface-1 relative overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-5xl md:text-6xl font-bold mb-6 text-text-primary font-display">
              Featured Stories
            </h2>
            <p className="text-xl text-text-secondary max-w-3xl mx-auto">
              The most important updates and announcements from our family association
            </p>
          </div>

          {/* Main Featured Article */}
          <div className="mb-16">
            {displayFeatured.length > 0 ? (
              displayFeatured.slice(0, 1).map((announcement) => {
                const typeDisplay = getTypeDisplay(announcement.type);
                const classes = getWarmValueClasses();
                // const IconComponent = typeDisplay.icon;

                return (
                  <div key={announcement.id} className="group">
                    <div className={`${classes.cardBg} rounded-3xl overflow-hidden border ${classes.border} shadow-sm ${classes.hoverShadow} transition-shadow`}>
                      <div className="md:flex">
                        <div className="md:w-1/2">
                          <div className="w-full h-64 md:h-full bg-surface-2 flex items-center justify-center">
                            <div className={`w-24 h-24 rounded-full ${classes.iconBg} flex items-center justify-center shadow-sm`}>
                              <typeDisplay.icon className="w-12 h-12 text-white" />
                            </div>
                          </div>
                        </div>
                        <div className="md:w-1/2 p-8 md:p-12">
                          <div className="flex items-center space-x-4 mb-4">
                            <span className={`px-3 py-1 ${classes.iconBg} text-white text-xs font-semibold rounded-full`}>
                              {typeDisplay.label}
                            </span>
                            <div className="flex items-center text-text-secondary text-sm">
                              <Calendar className="w-4 h-4 mr-2" />
                              {formatDate(announcement.createdAt)}
                            </div>
                          </div>

                          <h3 className="text-3xl font-bold mb-4 text-text-primary font-display">
                            {announcement.title}
                          </h3>

                          <div className="mb-6">
                            <ExpandableContent
                              content={announcement.content}
                              maxLength={300}
                              proseColor="amber"
                              className="text-lg leading-relaxed text-text-secondary"
                              cardId={`featured-main-${announcement.id}`}
                              expandedCards={expandedCards}
                              onToggleExpand={toggleCardExpansion}
                            />
                          </div>

                          <div className="flex items-center justify-between">
                            <div className="flex items-center text-text-tertiary text-sm">
                              <Users className="w-4 h-4 mr-2" />
                              Family Admin
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-16">
                <div className="w-20 h-20 bg-brand rounded-full flex items-center justify-center mx-auto mb-6">
                  <Info className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-2 text-text-primary font-display">
                  No Featured Updates
                </h3>
                <p className="text-text-secondary text-lg">
                  Check back soon for featured family announcements.
                </p>
              </div>
            )}
          </div>

          {/* Secondary Featured Articles */}
          <div className="grid md:grid-cols-2 gap-8">
            {displayFeatured.slice(1).map((announcement) => {
              const typeDisplay = getTypeDisplay(announcement.type);
              const classes = getWarmValueClasses();
              // const IconComponent = typeDisplay.icon;

              return (
                <div key={announcement.id} className="group">
                  <div className={`${classes.cardBg} rounded-3xl overflow-hidden border ${classes.border} shadow-sm ${classes.hoverShadow} transition-shadow`}>
                    <div className="w-full h-48 bg-surface-2 flex items-center justify-center">
                      <div className={`w-16 h-16 rounded-full ${classes.iconBg} flex items-center justify-center shadow-sm`}>
                        <typeDisplay.icon className="w-8 h-8 text-white" />
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center space-x-4 mb-4">
                        <span className={`px-3 py-1 ${classes.iconBg} text-white text-xs font-semibold rounded-full`}>
                          {typeDisplay.label}
                        </span>
                        <div className="flex items-center text-text-secondary text-sm">
                          <Clock className="w-4 h-4 mr-2" />
                          {formatDate(announcement.createdAt)}
                        </div>
                      </div>

                      <h3 className="text-xl font-bold mb-3 text-text-primary font-display">
                        {announcement.title}
                      </h3>

                      <div className="mb-4">
                        <ExpandableContent
                          content={announcement.content}
                          maxLength={200}
                          proseColor="amber"
                          className="text-text-secondary leading-relaxed"
                          cardId={`featured-secondary-${announcement.id}`}
                          expandedCards={expandedCards}
                          onToggleExpand={toggleCardExpansion}
                        />
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-text-tertiary text-sm">
                          Family Admin
                        </span>
                        <div className="flex space-x-2">
                          <button className="p-2 text-text-tertiary hover:text-brand transition-colors">
                            <Heart className="w-4 h-4" />
                          </button>
                          <button className="p-2 text-text-tertiary hover:text-brand transition-colors">
                            <Share2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Recent News Section */}
      <section className="py-16 bg-surface-0 relative overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-5xl md:text-6xl font-bold mb-6 text-text-primary font-display">
              Recent Updates
            </h2>
            <p className="text-xl text-text-secondary max-w-3xl mx-auto">
              Stay informed with our latest family news and community updates
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {displayRecent.length > 0 ? (
              displayRecent.map((announcement) => {
                const typeDisplay = getTypeDisplay(announcement.type);
                const classes = getWarmValueClasses();
                // const IconComponent = typeDisplay.icon;

                return (
                  <div key={announcement.id} className="group">
                    <div className={`${classes.cardBg} rounded-2xl p-6 border ${classes.border} shadow-sm ${classes.hoverShadow} transition-shadow`}>
                      <div className="flex items-center space-x-2 mb-4">
                        <span className={`px-2 py-1 ${classes.iconBg} text-white text-xs font-medium rounded`}>
                          {typeDisplay.label}
                        </span>
                        <div className="flex items-center text-text-tertiary text-xs">
                          <Clock className="w-3 h-3 mr-1" />
                          {formatDate(announcement.createdAt)}
                        </div>
                      </div>

                      <h3 className="text-lg font-bold mb-3 text-text-primary font-display">
                        {announcement.title}
                      </h3>

                      <div className="mb-4">
                        <ExpandableContent
                          content={announcement.content}
                          maxLength={150}
                          proseColor="amber"
                          className="text-sm text-text-secondary leading-relaxed"
                          cardId={`recent-${announcement.id}`}
                          expandedCards={expandedCards}
                          onToggleExpand={toggleCardExpansion}
                        />
                      </div>

                      <div className="flex items-center justify-between pt-4 border-t border-border">
                        <span className="text-text-tertiary text-xs">
                          Family Admin
                        </span>
                        <div className="flex space-x-2">
                          <button className="p-1 text-text-tertiary hover:text-brand transition-colors">
                            <MessageCircle className="w-3 h-3" />
                          </button>
                          <button className="p-1 text-text-tertiary hover:text-brand transition-colors">
                            <Share2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="col-span-full text-center py-16">
                <div className="w-16 h-16 bg-brand rounded-full flex items-center justify-center mx-auto mb-6">
                  <Info className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-2 text-text-primary font-display">
                  No Recent Updates
                </h3>
                <p className="text-text-secondary">
                  Check back soon for the latest family news.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>


      {/* Join Family Portal CTA Section */}
      <section className="py-16 bg-surface-1 relative overflow-hidden">
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <div className="bg-surface-0 rounded-3xl p-12 border border-border shadow-sm">
            <div className="w-20 h-20 bg-brand rounded-full flex items-center justify-center mx-auto mb-8 shadow-sm">
              <Heart className="w-10 h-10 text-white" />
            </div>

            <h2 className="text-4xl font-bold mb-6 text-text-primary font-display">
              Join Our Family Portal
            </h2>

            <p className="text-lg text-text-secondary mb-8 leading-relaxed max-w-2xl mx-auto">
              Access exclusive family content, reunion details, payment management, family directory, and special announcements for verified family members.
            </p>

            <button
              onClick={() => navigate('/login')}
              className="bg-brand hover:bg-brand-hover text-white px-12 py-4 rounded-full text-xl font-semibold shadow-sm hover:shadow-md transition-all inline-flex items-center space-x-3"
            >
              <Users className="w-6 h-6" />
              <span>Access Family Portal</span>
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-surface-2 py-16 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute bottom-0 left-0 w-full h-px bg-border"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Logo size="lg" className="rounded-2xl mx-auto mb-8 shadow-sm" />

          <h3 className="text-2xl font-bold mb-4 text-text-primary font-display">
            The Redd Family Association
          </h3>

          <p className="text-text-secondary text-lg mb-8 max-w-2xl mx-auto">
            Where faith guides our path, family strengthens our hearts, and every generation is blessed in our continuing legacy.
          </p>

          <div className="flex justify-center space-x-8 text-text-tertiary">
            {[Heart, Users, BookOpen, Award].map((Icon, index) => (
              <Icon key={index} className="w-6 h-6 hover:text-brand transition-colors cursor-pointer" />
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default NewsPageWarm;
